/**
 * lib/orderConfirm.ts -- the one confirmation step every order surface uses
 * (OrderTicket PLACE ORDER, FIRE ALL BROKERS, the DOM ladder).
 *
 * The decisions live in lib/tradingMode.ts (pure, tested); this file only
 * fetches the router's answer and asks the trader.
 *
 * ORDER OF OPERATIONS, AND WHY.
 *
 *   1. Read GET /api/broker/status FRESH, at the moment of confirmation, with
 *      the same per-broker credentials the order will carry. A status read at
 *      mount can be stale: the vault may have been re-unlocked with a live
 *      entry since.
 *   2. Mode UNKNOWN (status unreadable) or DISCONNECTED: refuse. We do not
 *      default to paper -- the dangerous direction is one-way.
 *   3. Real money (live, or IBKR which cannot be proven paper): the trader must
 *      TYPE "LIVE". That typed answer is the only thing that sets
 *      confirmed_mode: "live". A click-through OK dialog is not used for real
 *      money because it is exactly what a reflex dismisses.
 *   4. Paper / demo: an OK dialog, and confirmed_mode echoes the mode shown,
 *      so the router refuses the order if the account turned out to be live.
 */
import toast from 'react-hot-toast';
import { api } from '@/lib/api';
import {
  type BrokerStatus, type ModeInfo,
  modeFromStatus, confirmedModeFor, confirmedModeForMany, isExplicitLiveConfirmation, LIVE_CONFIRM_PHRASE,
} from '@/lib/tradingMode';

/** GET /api/broker/status, or null when it could not be read (= mode UNKNOWN). */
export async function fetchBrokerStatus(): Promise<{ status: BrokerStatus | null; error: string | null }> {
  try {
    return { status: await api<BrokerStatus>('/api/broker/status'), error: null };
  } catch (e) {
    return { status: null, error: (e as Error).message || 'broker status unavailable' };
  }
}

export interface ConfirmResult {
  confirmedMode: 'live' | 'paper' | 'demo';
  info:          ModeInfo;
  status:        BrokerStatus | null;
}

function refuseUnknown(broker: string, info: ModeInfo, err: string | null): null {
  if (!info.known) {
    toast.error(`Cannot determine whether ${broker} is PAPER or LIVE${err ? ` (${err})` : ''}. Nothing was sent.`, { duration: 9000 });
  } else {
    toast.error(`${broker} is not connected -- unlock its vault entry. Nothing was sent.`, { duration: 7000 });
  }
  return null;
}

/**
 * Confirm one order to one broker. Returns null when it must not be sent.
 * `summary` is the order description (side, qty, symbol, price, ...).
 */
export async function confirmOrderFor(broker: string, summary: string): Promise<ConfirmResult | null> {
  const { status, error } = await fetchBrokerStatus();
  const info = modeFromStatus(status, broker);
  if (!info.known || !info.connected) return refuseUnknown(broker, info, error);

  let liveConfirmed = false;
  if (info.realMoney) {
    const typed = window.prompt(
      `*** LIVE ACCOUNT -- REAL MONEY ***\n` +
      `${broker.toUpperCase()}: ${info.label}\n\n${summary}\n\n` +
      `Type ${LIVE_CONFIRM_PHRASE} to confirm live trading. Anything else cancels.`,
      '',
    );
    liveConfirmed = isExplicitLiveConfirmation(typed);
    if (!liveConfirmed) {
      if (typed !== null) toast('Live trading NOT confirmed -- nothing was sent.', { icon: '!' });
      return null;
    }
  } else {
    const ok = window.confirm(`${info.label} account -- ${broker.toUpperCase()} (your own, via vault)\n\n${summary}\n\nPlace this order?`);
    if (!ok) return null;
  }
  const confirmedMode = confirmedModeFor(info, liveConfirmed);
  return confirmedMode ? { confirmedMode, info, status } : null;
}

/** Confirm a fan-out. Any real-money leg makes the whole request need a typed LIVE. */
export async function confirmFanOut(brokers: string[], summary: string): Promise<{ confirmedMode: 'live' | 'paper' | 'demo'; infos: ModeInfo[] } | null> {
  const { status, error } = await fetchBrokerStatus();
  const infos = brokers.map((b) => modeFromStatus(status, b));
  const bad = brokers.filter((_, i) => !infos[i].known || !infos[i].connected);
  if (bad.length) {
    toast.error(`Cannot confirm PAPER/LIVE for: ${bad.join(', ')}${error ? ` (${error})` : ''}. Nothing was sent.`, { duration: 9000 });
    return null;
  }
  const venues = brokers.map((b, i) => `${b}=${infos[i].label}`).join(', ');
  const anyReal = infos.some((i) => i.realMoney);
  let liveConfirmed = false;
  if (anyReal) {
    const typed = window.prompt(
      `*** ONE OR MORE LIVE ACCOUNTS -- REAL MONEY ***\n\n${summary}\nVenues: ${venues}\n\n` +
      `Type ${LIVE_CONFIRM_PHRASE} to confirm live trading. Anything else cancels.`,
      '',
    );
    liveConfirmed = isExplicitLiveConfirmation(typed);
    if (!liveConfirmed) {
      if (typed !== null) toast('Live trading NOT confirmed -- nothing was sent.', { icon: '!' });
      return null;
    }
  } else if (!window.confirm(`${summary}\nVenues: ${venues}\n\nFire all?`)) {
    return null;
  }
  const confirmedMode = confirmedModeForMany(infos, liveConfirmed);
  return confirmedMode ? { confirmedMode, infos } : null;
}
